/**
 * shared/pmove/melee.ts — melee lunge ("charge") movement.
 *
 * OWNER: PMOVE agent.
 * [T5 bg_pmove.cpp: PM_MeleeChargeStart / IsValid / Clear / PM_CalcMeleeChargeTime /
 * PM_CheckMeleeCharge / PM_MeleeChargeMove]; weapons-mp.md §5: charge velocity = 2*dist/chargeTime
 * toward meleeChargeYaw, chargeTime = meleeChargeDelay when dist > player_meleeRange (64) else
 * meleeDelay; the speed decays linearly to 0 over chargeTime (so the lunge covers exactly `dist`).
 * T6 adds player_meleeChargeFriction / player_meleeChargeMaxSpeed (defaults unknown) -> ESTIMATE:
 * the T5 model below.
 *
 * Hand-off with PM_Weapon (weapons agent): when a lunge swing starts, PM_Weapon calls
 * `meleeChargeStart(ps, yawDeg, dist)` (or sets ps.meleeChargeYaw/Dist with meleeChargeTime = 0).
 * The next PmoveSingle validates it (on the ground, not mantling/on a ladder) and runs the charge;
 * pm_flags 0x20000 (PMF.SPECIAL_FRICTION = T5 PMF_MELEE_CHARGE) is set while it lasts.
 */
import { ENTITYNUM_NONE } from '../constants';
import type { Vec3 } from '../math';
import { PMF, PmType, WeaponId, type PlayerState } from '../types';
import { projectVelocity, stepSlideMove } from './slide';
import { setMovementDir } from './pmove';
import { PM_MELEE_RANGE, yawVectors2D, type Pm } from './pm';

/** Request a lunge (called by PM_Weapon when the charge swing starts). dist 0..255 u. */
export function meleeChargeStart(ps: PlayerState, yawDeg: number, dist: number): void {
  ps.meleeChargeYaw = yawDeg;
  ps.meleeChargeDist = Math.max(0, Math.min(255, Math.round(dist)));
  ps.meleeChargeTime = 0;
}

/** PM_MeleeChargeClear [T5 bg_pmove.cpp:1515]. */
export function meleeChargeClear(ps: PlayerState): void {
  ps.pm_flags &= ~PMF.SPECIAL_FRICTION;
  ps.meleeChargeYaw = 0;
  ps.meleeChargeDist = 0;
  ps.meleeChargeTime = 0;
}

/** True while a lunge is moving the player. */
export function isMeleeCharging(ps: PlayerState): boolean {
  return (ps.pm_flags & PMF.SPECIAL_FRICTION) !== 0;
}

/** PM_MeleeChargeIsValid [T5 bg_pmove.cpp:1535] (minus the flag test). */
function chargeValid(ps: PlayerState): boolean {
  if (!ps.meleeChargeDist) return false;
  if (ps.pm_type !== PmType.NORMAL) return false;
  if (ps.pm_flags & (PMF.LADDER | PMF.MANTLE)) return false;
  return ps.groundEntityNum !== ENTITYNUM_NONE; // !PM_IsInAir
}

const chargeDir: Vec3 = [0, 0, 0];

/** PM_CalcMeleeChargeTime [T5 bg_pmove.cpp:1575]. */
function calcMeleeChargeTime(pm: Pm): void {
  const ps = pm.ps;
  const held = pm.ctx.weaponDef(ps.weapon);
  const def = held.useAsMelee ? held : pm.ctx.weaponDef(ps.meleeWeapon !== WeaponId.NONE ? ps.meleeWeapon : WeaponId.KNIFE);
  const chargeTimeMs = ps.meleeChargeDist > PM_MELEE_RANGE ? def.meleeChargeDelayMs : def.meleeDelayMs;
  if (chargeTimeMs > 0) {
    const chargeVel = (ps.meleeChargeDist / (chargeTimeMs * 0.001)) * 2;
    yawVectors2D(ps.meleeChargeYaw, chargeDir);
    ps.velocity[0] = chargeVel * chargeDir[0];
    ps.velocity[1] = chargeVel * chargeDir[1];
    ps.meleeChargeTime = chargeTimeMs;
  } else {
    meleeChargeClear(ps);
  }
}

/** PM_CheckMeleeCharge [T5 bg_pmove.cpp:5507] + the implicit PM_MeleeChargeStart. */
export function checkMeleeCharge(pm: Pm): void {
  const ps = pm.ps;
  if (!(ps.pm_flags & PMF.SPECIAL_FRICTION)) {
    if (ps.meleeChargeDist && ps.meleeChargeTime === 0 && chargeValid(ps)) {
      ps.pm_flags |= PMF.SPECIAL_FRICTION;
    } else {
      if (ps.meleeChargeDist || ps.meleeChargeTime) meleeChargeClear(ps);
      return;
    }
  }
  if (!ps.meleeChargeTime) calcMeleeChargeTime(pm);
}

const dir: Vec3 = [0, 0, 0];
/** PM_MeleeChargeMove [T5 bg_pmove.cpp:5527]: linear speed decay over meleeChargeTime. */
export function meleeChargeMove(pm: Pm): void {
  const ps = pm.ps;
  const v = ps.velocity;
  const speed = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  let newSpeed = speed;
  if (speed > 0) {
    dir[0] = v[0] / speed; dir[1] = v[1] / speed; dir[2] = v[2] / speed;
    newSpeed = speed - (speed / (ps.meleeChargeTime * 0.001)) * pm.frametime;
    if (newSpeed < 0) newSpeed = 0;
    const mid = (speed + newSpeed) * 0.5; // trapezoid: move with the mean speed of the frame
    v[0] = mid * dir[0]; v[1] = mid * dir[1]; v[2] = mid * dir[2];
  } else {
    dir[0] = dir[1] = dir[2] = 0;
  }
  projectVelocity(v, pm.groundTrace.normal, v);
  if (v[0] !== 0 || v[1] !== 0) stepSlideMove(pm, false);
  v[0] = newSpeed * dir[0]; v[1] = newSpeed * dir[1]; v[2] = newSpeed * dir[2];
  ps.meleeChargeTime -= pm.msec;
  if (ps.meleeChargeTime <= 0) {
    v[0] = 0; v[1] = 0;
    meleeChargeClear(ps);
  }
  setMovementDir(pm);
}
